import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import ConfirmDeleteDocumentModal from "../Documents/ConfirmDeleteDocumentModal";
import DocumentViewer from "../Documents/DocumentViewer";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import DragAndDrop from "../DragAndDrop";
import Modal from "@mui/material/Modal";
import {extractNumberAndConvertToDate, formatFileSize} from "../HR/Candidate/Documents";
import {buttonStyles, typographyStyles} from "../HR/TableTemplate";
import {getDocumentFile} from "../../utilities/cvAPI";
import keycloak from "../../Keycloak";
import {showSaveError} from "../toastUtils";
import CircularProgress from "@mui/material/CircularProgress";
import {Alert, Snackbar} from "@mui/material";


export default function AgreementsTable({ documentNames, handleDocumentUpdate}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [documentList, setDocumentList] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [currentView, setCurrentView] = useState('table');
    const [enableSave, setEnableSave] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const columns = useMemo(() => [
        { id: "dateUploaded", label: "Date Uploaded" },
        { id: "name", label: "Name" },
        { id: "fileSize", label: "File Size" },
        { id: "fileStatus", label: "File Status" },
        { id: "popOver", label: "Action" }
    ], []);

    const tableHeaderStyle = {
        backgroundColor: '#DDDFDF',
        height: '56px'
    };

    const tableCellStyle = {
        fontWeight: '600',
        textAlign: 'left',
        padding: '8px 16px',
    };

    const tableContainerStyle = {
        borderRadius: "4px",
        boxShadow: "0px 0px 0px 1px #E0E0E0",
    };

    const buttonStyle = {
        float: "right",
        width: "89px",
        height: "40px",
        fontFamily: "Poppins",
    };

    const cancelButtonStyle = {
        ...buttonStyle,
        color: "#FF6600",
        borderColor: "#FF6600",
    };

    const saveButtonStyle = {
        ...buttonStyle,
        backgroundColor: "#FF6600",
        color: "#FFFFFF",
        marginLeft: "auto",
    };

    const modalBoxStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "50%",
        height: "auto",
        bgcolor: "background.paper",
        boxShadow: -100,
        p: 2,
    };

    const renderTableCell = (content) => <TableCell scope="row">{content}</TableCell>;

    const fetchDocuments = useCallback(async () => {
        try {
            const newDocuments = await Promise.all(documentNames.map(async (doc) => {
                if (!doc.s3FileName) {
                    return {
                        id: doc.id,
                        dateModified: new Date(doc.dateUploaded).toLocaleString('en-ZA', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }),
                        name: doc.fileName,
                        fileSize: "N/A",
                        s3FileName: "N/A",
                        blob: null,
                        fileStatus: "invalid reference"
                    };
                }

                const blob = await getDocumentFile(doc.s3FileName, keycloak.token);

                return {
                    id: doc.id,
                    dateModified: extractNumberAndConvertToDate(doc.s3FileName).toLocaleString('en-ZA', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }),
                    name: doc.fileName,
                    fileSize: formatFileSize(blob.size),
                    s3FileName: doc.s3FileName,
                    blob,
                    fileStatus: "active"
                };
            }));
            setDocumentList(newDocuments);
        } catch (error) {
            showSaveError("Error fetching documents");
        }
    }, [documentNames]);

    useEffect(() => {
        const fetchDocumentsAsync = async () => {
            if (documentNames.length > 0) {
                try {
                    await fetchDocuments();
                } catch (error) {
                    console.error('Error fetching documents:', error);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        fetchDocumentsAsync();
    }, [fetchDocuments, documentNames]);

    const handleViewDocument = async (documentId) => {
        const document = documentList.find(doc => doc.id === documentId);
        const url = URL.createObjectURL(document.blob);
        setSelectedDocument({ ...document, url });
        setCurrentView('document');
    };

    const handleModalTrigger = () => {
        if (currentView === 'table') {
            setShowModal(true);
        } else {
            setCurrentView('table');
            setSelectedDocument(null);
            setAnchorEl(null);
        }
    };

    const handleOptionsClick = (e, document) => {
        setAnchorEl(e.currentTarget);
        setSelectedDocument(document);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const openDeleteModal = () => {
        handleClose();
        setShowDeleteModal(true);
    };

    const closeModal = () => setShowModal(false);

    const handleUploadedFiles = (files) => {
        if (!files || files.length === 0) {
            return;
        }

        const existingDocumentNames = documentList.map(doc => doc.name.toLowerCase());

        const filesArray = Array.from(files);

        const nonDuplicateFiles = filesArray.filter(file => {
            if (existingDocumentNames.includes(file.name.toLowerCase())) {
                setSnackbarOpen(true);
                return false;
            }
            return true;
        });

        if (nonDuplicateFiles.length > 0) {
            setUploadedFiles(nonDuplicateFiles);
            setEnableSave(true);
        }
    };

    const handleDeleteConfirmation = () => {
        if (selectedDocument) {
            handleDeleteDocument(selectedDocument);
        }
        closeDeleteModal();
    };

    const handleDeleteDocument = async (documentToDelete) => {
        const updatedDocumentList = documentList.reduce((acc, doc) => {
            if (documentToDelete.id && doc.id === documentToDelete.id) {
                return [...acc, { ...doc, fileStatus: "pending delete" }];
            }

            if (!documentToDelete.id && doc.name === documentToDelete.name) {
                return acc;
            }
            return [...acc, doc];
        }, []);

        setDocumentList(updatedDocumentList);
        handleDocumentUpdate(updatedDocumentList);
    };

    const handleSave = async () => {
        const newUploadedDocs = uploadedFiles.map((file) => ({
            id: null,
            dateModified: new Date().toLocaleString('en-ZA', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
            name: file.name,
            fileSize: formatFileSize(file.size),
            fileStatus: "pending save",
            blob: file,
        }));
        const updatedDocumentList = [...documentList, ...newUploadedDocs];
        setDocumentList(updatedDocumentList);
        handleDocumentUpdate(updatedDocumentList);
        closeModal();
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress style={{ color: '#FF6600' }} data-testid="loading-spinner"/>
            </div>
        );
    }

    return (
        <div data-testid="documentContainer">
            <div data-testid="table-div">
                <ConfirmDeleteDocumentModal
                    showModal={showDeleteModal}
                    closeModal={closeDeleteModal}
                    handleDeleteConfirmation={handleDeleteConfirmation}
                />
                <Modal open={showModal} aria-labelledby="modal-title" aria-describedby="modal-description">
                    <Box sx={modalBoxStyle}>
                        <Typography variant="h4" component="h2">
                            Upload documents
                        </Typography>
                        <Grid container direction={"row"} alignItems={"flex-end"} padding={2} spacing={2}>
                            <Grid item xs={12}>
                                <DragAndDrop
                                    inputUpdated={handleUploadedFiles}
                                />
                            </Grid>
                            <Grid item xs={10}>
                                <Button variant="outlined" data-testid={"cancel"} onClick={closeModal}
                                        style={cancelButtonStyle}>
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid item xs={2}>
                                <Button onClick={handleSave} data-testid={"save"} style={saveButtonStyle}
                                        disabled={!enableSave}>
                                    Save
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Modal>
                <Snackbar
                    open={snackbarOpen} e
                    onClose={() => setSnackbarOpen(false)}
                    data-testid={"snackbar"}
                >
                    <Alert onClose={() => setSnackbarOpen(false)} severity="error">
                        Document with that name exists, please rename the document and try again
                    </Alert>
                </Snackbar>
                <Grid container direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
                    <Grid item xs={10}>
                        <div className="HeadingTypography" data-testid="documentHeading">Agreements & Documents</div>
                    </Grid>
                    <Grid item xs={2}>
                        <Button className={"w-100"}
                            data-testid={"doc-table-button"}
                            onClick={handleModalTrigger}
                            variant="outlined"
                            style={{
                                float: "right",
                                backgroundColor: "#FF6600",
                                borderColor: "#FF6600",
                                fontFamily: "Poppins",
                                color: "#FFFFFF",
                            }}
                        >
                            {currentView === 'table' ? (
                                <>
                                    <AddIcon style={{marginRight: "5px"}}/> Add
                                </>
                            ) : "View List"}
                        </Button>
                    </Grid>
                    <Grid container direction="column" justifyContent={"flex-start"} alignItems={"stretch"}
                          spacing={2} padding={5} className="ParagraphPadding"
                    >
                        {currentView === 'table' ? (
                            documentList.length > 0 && (
                                <Grid item xs={12}>
                                    <TableContainer className="mt-4" component={Paper} style={tableContainerStyle}>
                                        <Table>
                                            <TableHead>
                                                <TableRow style={tableHeaderStyle}>
                                                    {columns.map((columnData) => (
                                                        <TableCell key={columnData.id} style={tableCellStyle}>
                                                            {columnData.label}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {documentList.map((row, index) => (
                                                    <TableRow key={index}
                                                              sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                                        {renderTableCell(row.dateModified)}
                                                        {renderTableCell(row.name)}
                                                        {renderTableCell(row.fileSize)}
                                                        {renderTableCell(row.fileStatus)}
                                                        <TableCell scope="row">
                                                            <Button data-testid={`options${index}`}
                                                                    align="left"
                                                                    variant="text"
                                                                    style={{color: 'black'}}
                                                                    onClick={(event) => handleOptionsClick(event, row)}
                                                            >
                                                                <MoreVertOutlinedIcon/>
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Popover data-testid={'popover'} open={Boolean(anchorEl)} anchorEl={anchorEl}
                                             onClose={handleClose}
                                             anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} elevation={1}>
                                        <Typography sx={typographyStyles}>
                                            <Button sx={buttonStyles} onClick={() => handleViewDocument(selectedDocument.id)}
                                                    disabled={selectedDocument?.fileStatus === "pending save" || selectedDocument?.fileStatus === "invalid reference"}>View</Button>
                                        </Typography>
                                        <Typography sx={typographyStyles}>
                                            <Button sx={buttonStyles} data-testid={"deleteDocument"}
                                                    onClick={openDeleteModal}>Delete</Button>
                                        </Typography>
                                    </Popover>
                                </Grid>
                            )
                        ) : (
                            <div>
                                <DocumentViewer selectedDocument={selectedDocument}/>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}